import { useMemo } from "react";
import { Anchor } from "@mantine/core";
import { Link } from "react-router-dom";
import EcosystemHeadCorridorCard from "@/components/ecosystem-head/EcosystemHeadCorridorCard";
import type { EcosystemHeadNavState } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import { guestCorridorProfileHref } from "@/pages/discover/discoverGuest.const";
import {
  sectorCorridorProfileHref,
  sectorCorridorToCardData,
} from "@/pages/ecosystem-heads/ecosystemHeadCorridorCard.util";
import EcosystemCorridorPlaceholderCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPlaceholderCard";
import EcosystemCorridorPromoCard from "@/pages/ecosystem-heads/components/EcosystemCorridorPromoCard";
import EcosystemExploreCardCarousel, {
  EcosystemExploreCarouselSlide,
} from "@/pages/ecosystem-heads/components/EcosystemExploreCardCarousel";
import { buildEcosystemCorridorEntries, type CorridorListEntry } from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { resolvePlaceholderCorridorsForSector } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import type {
  SectorEcosystemCorridorHead,
  SectorEcosystemOverview,
} from "../../../../../../shared/sectorEcosystemOverview";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import SectorDashboardCard from "./SectorDashboardCard";

const CARDS_PER_PACK = 1;
/** One promo slide after every N corridor cards. */
const PROMO_EVERY_N_PACKS = 4;

type SectorEcosystemHeadsCarouselProps = {
  sectorSlug: string;
  overview: SectorEcosystemOverview;
  headsHref: string;
  headsNavState?: EcosystemHeadNavState;
  isAuthenticated: boolean;
};

type CarouselCorridorItem =
  | { kind: "real"; corridor: SectorEcosystemCorridorHead }
  | { kind: "placeholder"; corridor: ComingSoonEcosystemCorridor };

type SectorCorridorEntry = CorridorListEntry<CarouselCorridorItem>;

type SectorCorridorPackItem = Extract<SectorCorridorEntry, { type: "item" }>;

type SectorCarouselPage =
  | { type: "corridorPack"; key: string; items: SectorCorridorPackItem[] }
  | { type: "promo"; key: string };

function buildSectorCarouselPages(slides: SectorCorridorEntry[]): SectorCarouselPage[] {
  const packable = slides.filter((entry): entry is SectorCorridorPackItem => entry.type === "item");
  const packs: SectorCarouselPage[] = [];

  for (let i = 0; i < packable.length; i += CARDS_PER_PACK) {
    const items = packable.slice(i, i + CARDS_PER_PACK);
    const first = items[0]!;
    packs.push({
      type: "corridorPack",
      key: `corridor-pack-${first.key}`,
      items,
    });
  }

  // Always show the promo when there are no corridor cards.
  if (packs.length === 0) {
    return [{ type: "promo", key: "promo" }];
  }

  const pages: SectorCarouselPage[] = [];
  packs.forEach((pack, index) => {
    pages.push(pack);
    if ((index + 1) % PROMO_EVERY_N_PACKS === 0) {
      pages.push({ type: "promo", key: `promo-after-${index}` });
    }
  });

  // Cadence only inserts after every N packs — still guarantee one promo
  // when there is only a single corridor card.
  if (!pages.some((page) => page.type === "promo")) {
    pages.push({ type: "promo", key: "promo" });
  }

  return pages;
}

export default function SectorEcosystemHeadsCarousel({
  sectorSlug,
  overview,
  headsHref,
  headsNavState,
  isAuthenticated,
}: SectorEcosystemHeadsCarouselProps) {
  const realCorridors = overview.ecosystemHeadCorridors;
  const hasRealCorridors = realCorridors.length > 0;
  const placeholderCorridors = useMemo(
    () => resolvePlaceholderCorridorsForSector(sectorSlug, overview.title, hasRealCorridors),
    [sectorSlug, overview.title, hasRealCorridors],
  );

  const slides = useMemo(() => {
    const realItems: CarouselCorridorItem[] = realCorridors.map((corridor) => ({
      kind: "real" as const,
      corridor,
    }));
    const placeholderItems: CarouselCorridorItem[] = placeholderCorridors.map((corridor) => ({
      kind: "placeholder" as const,
      corridor,
    }));

    return buildEcosystemCorridorEntries(
      realItems,
      placeholderItems,
      (item) =>
        item.kind === "real"
          ? `real-${item.corridor.ecosystemHeadUserId}-${item.corridor.country}-${item.corridor.targetCountry}`
          : `placeholder-${item.corridor.id}`,
      { promoEveryN: 0 },
    );
  }, [realCorridors, placeholderCorridors]);

  const pages = useMemo(() => buildSectorCarouselPages(slides), [slides]);

  return (
    <SectorDashboardCard
      fillHeight={false}
      title="Ecosystem corridor"
      subtitle="Leaders driving growth in this ecosystem."
      headerAction={
        slides.length > 0 ? (
          <Anchor component={Link} to={headsHref} state={headsNavState} size="xs" fw={600} c="primary.7">
            View all corridors →
          </Anchor>
        ) : undefined
      }
    >
      {pages.length === 0 ? null : (
        <EcosystemExploreCardCarousel itemCount={pages.length}>
          {pages.map((page) => {
            if (page.type === "promo") {
              return (
                <EcosystemExploreCarouselSlide key={page.key}>
                  <EcosystemCorridorPromoCard variant="explore" fullWidth />
                </EcosystemExploreCarouselSlide>
              );
            }

            const entry = page.items[0];
            if (!entry) return null;

            if (entry.item.kind === "real") {
              const corridor = entry.item.corridor;
              const href = isAuthenticated
                ? sectorCorridorProfileHref(corridor)
                : guestCorridorProfileHref(corridor.industry, corridor.ecosystemHeadUserId);
              const linkState = isAuthenticated
                ? headsNavState
                : { discoverReturnTo: `/sector/${sectorSlug}` };
              return (
                <EcosystemExploreCarouselSlide key={page.key}>
                  <EcosystemHeadCorridorCard
                    head={sectorCorridorToCardData(corridor)}
                    fullWidth
                    href={href}
                    linkState={linkState}
                  />
                </EcosystemExploreCarouselSlide>
              );
            }

            return (
              <EcosystemExploreCarouselSlide key={page.key}>
                <EcosystemCorridorPlaceholderCard
                  corridor={entry.item.corridor}
                  variant="explore"
                  fullWidth
                />
              </EcosystemExploreCarouselSlide>
            );
          })}
        </EcosystemExploreCardCarousel>
      )}
    </SectorDashboardCard>
  );
}
